import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'

export interface User {
    id: number
    login: string
    email: string
    first_name?: string
    last_name?: string
    role: string
    roles?: Array<{ id?: number; name?: string; role_name?: string }>
    tenant_id?: string
    organization_id?: string
    organization_name?: string
    active: boolean
    created_at: string
    last_login_at?: string
}

/**
 * Normalize the /api/auth/me response so `role` is always a string.
 * The backend may return `role` (string), `roles` (array of objects),
 * or neither — we fall back to 'member' to prevent downstream crashes.
 */
function normalizeUser(raw: Record<string, unknown>): User {
    const user = raw as unknown as User
    if (!user.role || typeof user.role !== 'string') {
        const roles = (raw.roles ?? []) as Array<{ name?: string; role_name?: string }>
        user.role = roles[0]?.name ?? roles[0]?.role_name ?? 'member'
    }
    return user
}

/* ───────────────────────────────────────── */
/* Get Current User                         */
/* GET /api/auth/me                         */
/* ───────────────────────────────────────── */

export const useCurrentUser = () =>
    useQuery<User>({
        queryKey: ['currentUser'],
        queryFn: async () => {
            const { data } = await apiClient.get('/api/auth/me')
            return normalizeUser(data as Record<string, unknown>)
        },
    })

/* ───────────────────────────────────────── */
/* Update User Profile                      */
/* PUT /api/auth/users/{user_id}            */
/* ───────────────────────────────────────── */

interface UpdateUserPayload {
    userId: number
    first_name?: string
    last_name?: string
    email?: string
}

export const useUpdateUser = () => {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: async (payload: UpdateUserPayload) => {
            const { userId, ...body } = payload
            const { data } = await apiClient.put(`/api/auth/users/${userId}`, body)
            return data
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['currentUser'] })
        },
    })
}

/* ───────────────────────────────────────── */
/* Change Password                          */
/* POST /api/auth/users/{user_id}/change-password */
/* ───────────────────────────────────────── */

interface ChangePasswordPayload {
    userId: number
    current_password: string
    new_password: string
    confirm_password?: string
}

export const useChangePassword = () =>
    useMutation({
        mutationFn: async (payload: ChangePasswordPayload) => {
            const { userId, confirm_password, ...body } = payload
            const { data } = await apiClient.post(
                `/api/auth/users/${userId}/change-password`,
                body
            )
            return data
        },
    })